"use client";

import { useTranslations } from "next-intl";
import { BlurImage, ProductPrice } from "@/components/core";
import { DirectionalChevron } from "@/components/core/DirectionalChevron";
import { Badge } from "@/components/ui";
import { Link } from "@/i18n/navigation";
import { useDateFormatter } from "@/lib/i18n/date";
import { cn } from "@/lib/utils";
import type { components } from "@/types/api";

type UserPurchasedOrderItemDto =
  components["schemas"]["UserPurchasedOrderItemDto"];

interface OrderItemProps {
  order: UserPurchasedOrderItemDto;
  className?: string;
}

export const OrderItem = ({ order, className }: OrderItemProps) => {
  const t = useTranslations("orders");
  const df = useDateFormatter();
  return (
    <div
      className={cn(
        "pt-8 px-4 pb-2 flex gap-4 border-b border-stroke-light",
        className,
      )}
    >
      <BlurImage
        // `product_images` is typed as a non-optional array, so TS cannot flag
        // `[0]` being undefined — but a product with no images makes it exactly
        // that, and reading `.url` off it threw during render and took the whole
        // orders view down. Guard the index too, so the default image is reached.
        src={
          order.product?.product_images?.[0]?.url ||
          "/images/status/defult-purchase.png"
        }
        width={62}
        height={62}
        alt={order.product?.title || "order item"}
        wrapperClassName="aspect-square rounded-xs bg-bg-secondary"
        className="rounded-xs object-cover size-15.5"
      />

      <div className="space-y-3 w-full">
        <div className="flex items-center justify-between w-full">
          <p className="text-xs">
            <span className="text-text-secondary">{t("item.orderCode")}</span>
            <span className="font-bold">
              {order._order_id || order.id.slice(0, 8) || "-"}
            </span>
          </p>

          <Link
            href={`/profile/orders/${order.id}`}
            className="flex items-center gap-1 text-xs"
          >
            <span>{t("item.viewDetails")}</span>
            <DirectionalChevron size={16} />
          </Link>
        </div>
        <div className="flex items-center justify-between w-full text-xs">
          <p className="text-text-secondary">
            {order.date ? df.monthDay(new Date(order.date)) : "-"}
          </p>
          {order.shipped_at ? (
            <Badge
              variant={"outline"}
              size={"sm"}
              className="bg-transparent border-semantic-success text-semantic-success font-bold text-xs"
            >
              {t("status.shipped")}
            </Badge>
          ) : (
            <Badge
              variant={"outline"}
              size={"sm"}
              className="bg-transparent border-semantic-link text-semantic-link font-bold text-xs"
            >
              {t("status.processing")}
            </Badge>
          )}
        </div>
        <div className="flex items-center justify-end w-full">
          <div className="text-text-primary font-bold font-peyda">
            <ProductPrice
              seller_share_unit={order.paid_price?.seller_share_unit}
              seller_share_currency={order.paid_price?.seller_share_currency}
              seller_settlement_currency={
                (
                  order.paid_price as {
                    seller_settlement_currency?: "UVM" | "USDT";
                  }
                )?.seller_settlement_currency
              }
              bv={order.paid_price?.bv}
            />
          </div>
        </div>
      </div>
    </div>
  );
};
